import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReactFlow, Controls, useNodesState, useEdgesState, useReactFlow, applyNodeChanges} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import styled from 'styled-components';
import MotifIcon from '../../Common/BaseComponentWrappers/MotifIcon';
import Toast from '../../Common/BaseComponentWrappers/MotifToast';
import {IconoirPlus, IconoirMinus, IconoirFrameMinusIn, IconoirLock, IconoirLockSlash} from '@ey-xd/motif-icon';
import uuidv4 from 'uuid/v4';
import {labels} from '../../../util/utils';
import {
	flowchartNodeType,
	flowChartNodeConnectionPositions,
	flowchartConnectorTypes,
	reactFlowColumn,
	cssPixel,
	ToastMessageVariants,
	constAddEditFlowChartStep
} from '../../../util/uiconstants';
import {saveFlowChartConnection, removeFlowChartConnection} from '../../../actions/FlowChart/flowchartactions';
import {getCssClassNameForFlowChartSteps} from '../../../components/Common/scotPlacemat/flowUtils';
import useUndoRedo from './useUndoRedo';
import {clearErrors} from '../../../../src/actions/erroractions';
import {editStep} from '../../../actions/FlowChart/flowchartstepactions';
import ProgressBar from '../../Common/BaseComponentWrappers/MotifProgressBar';
import {set} from 'lodash';

const ReactFlowComponent = ({
	getFlowChartDetails,
	engagementId,
	documentId,
	flowchartId,
	nodeTypes,
	edgeTypes,
	data,
	initRef,
	markerEnd
}) => {
	const dispatch = useDispatch();
	const {undo, canUndo, takeSnapshot, resetRevert} = useUndoRedo();
	const [nodes, setNodes] = useNodesState([]);
	const [edges, setEdges, onEdgesChange] = useEdgesState([]);
	const {
		getIntersectingNodes,
		getNodes,
		fitView,
		setViewport,
		zoomIn,
		zoomOut,
		getEdges,
		updateNode,
		updateEdge
	} = useReactFlow();
	const isEditModelLoading = useSelector((state) => state.isLoading);
	const error = useSelector((state) => state.errors);
	const flowChart = useSelector((state) => state.flowChart);

	const [isLoading, setIsLoading] = useState(false);
	const [isFlowchartSelectable, setIsFlowchartSelectable] = useState(true);
	const [toastConfig, setToastConfig] = useState({
		isToastVisiible: false,
		toastMessage: '',
		toastMessageType: ''
	});
	const [isEdgeDeleting, setIsEdgeDeleting] = useState(false);
	const [isHandlesVisibileOnOtherNodes, setIsHandlesVisibileOnOtherNodes] = useState(false);
	const [connectingNode, setConnectingNode] = useState(null);

	const [isConcurrency, setIsConcurrency] = useState(true);
	const [isNodeDragStop, setIsNodeDragStop] = useState(true);
	const [isRendering, setIsRendering] = useState(true);

	const dragRef = useRef(null);

	useEffect(() => {
		// Automatically fit the chart to the screen on initial render
		fitView();
	}, [fitView]);

	useEffect(() => {
		setIsLoading(isEditModelLoading);
	}, [isEditModelLoading]);

	const onNodesChange = useCallback(
		(changes) => {
			setNodes((nds) => {
				const appliedChanges = applyNodeChanges(changes, nds);
				return sortParents(appliedChanges);
			});
		},
		[setNodes]
	);

	useEffect(() => {
		let edgeMap = new Map();

		flowChart?.data[0]?.flowchartSteps?.forEach((node) => {
			if (node.flowchartStepsTargetStep?.length > 0) {
				let uniqueEdges = [...new Set(node.flowchartStepsTargetStep)];
				uniqueEdges.forEach((connection) => {
					let sourceGuid = flowChart?.data[0]?.flowchartSteps?.find(
						(step) => step.id === connection.sourceFlowchartStepID
					)?.flowchartStepGuid;
					let targetGuid = flowChart?.data[0]?.flowchartSteps?.find(
						(step) => step.id === connection.targetFlowchartStepID
					)?.flowchartStepGuid;
					let sourceHandleId = flowChartNodeConnectionPositions
						?.find((pos) => pos.position === connection.sourceConnectorPosition)
						?.id?.toString();
					let targetHandleId = flowChartNodeConnectionPositions
						?.find((pos) => pos.position === connection.targetConnectorPosition)
						?.id?.toString();

					let edgeKey = `${sourceGuid}-${sourceHandleId}-${targetGuid}-${targetHandleId}`;

					if (!edgeMap.has(edgeKey)) {
						edgeMap.set(edgeKey, {
							id: uuidv4(),
							source: sourceGuid,
							sourceHandle: sourceHandleId,
							target: targetGuid,
							targetHandle: targetHandleId,
							type: 'custom-edge',
							markerEnd: connection.targetFlowchartConnectorTypeID == 2 ? 'dot-marker' : markerEnd,
							markerStart: connection.sourceFlowchartConnectorTypeID == 2 ? 'dot-marker' : markerEnd,
							data: {
								removeEdge: removeEdge,
								sourceFlowchartConnectorTypeID: connection.sourceFlowchartConnectorTypeID,
								targetFlowchartConnectorTypeID: connection.targetFlowchartConnectorTypeID,
								handleEdgePropertyUpdate: handleEdgePropertyUpdate,
								sourcePosition: connection.sourceConnectorPosition,
								targetPosition: connection.targetConnectorPosition
							}
						});
					}
				});
			}
		});

		setEdges(Array.from(edgeMap.values()));
	}, [flowChart]);

	useEffect(() => {
		let highestHeight = reactFlowColumn.defaultHighestHeight; //Default value for height

		let filteredNodes = data?.nodes?.filter((node) => node.type === flowchartNodeType.flowChartStep);
		if (_.size(filteredNodes) > 0) {
			let highestStepObject = filteredNodes.reduce((max, colNode) => {
				return colNode.position.y > max.position.y ? colNode : max;
			}, filteredNodes[0]);

			highestHeight = highestStepObject.position.y + reactFlowColumn.defaultGapBtwStepAndHeight;
		}

		data.nodes?.forEach((node) => {
			if (node.type !== flowchartNodeType.flowChartColumn) {
				node.dragHandle ='.drag-icon';
				node.data = {
					...node.data,
					onDeleteClick: onDeleteNode
				};
			} else {
				node.style = {
					width: reactFlowColumn.width,
					height: highestHeight
				};
				node.draggable = false;
				node.deletable = false;
				node.selectable = false;
			}
		});
		setNodes(data.nodes);
		setIsRendering(false);
		resetRevert();
	}, [data]);

	useEffect(() => {
		const timer = setTimeout(() => {
			resetToastConfig();
		}, 3000);

		return () => clearTimeout(timer);
	}, [toastConfig.isToastVisiible, error]);

	const setHandleVisibilityonConnectStart = (event, {nodeId}) => {
		updateConnectingNodeAmongList(nodeId);
		setConnectingNode(nodeId);
		setIsHandlesVisibileOnOtherNodes(true);
	};

	const setHandleVisibilityonConnectEnd = () => {
		setIsHandlesVisibileOnOtherNodes(false);
		updateConnectingNodeAmongList(connectingNode, true);
		setConnectingNode(null);
	};

	const updateConnectingNodeAmongList = (nodeId, reset = false) => {
		const updatedNodes = nodes.map((node) => {
			if (node.id === nodeId) {
				return {
					...node,
					data: {
						...node.data,
						connectingNode: reset ? null : nodeId
					}
				};
			}
			return node;
		});
		setNodes(updatedNodes);
	};

	const validateUpcomingConnection = (connection) => {
		const {source, target} = connection;
		const availableEdges = getEdges();
		let duplicateEdge = availableEdges.find((edge) => edge.source === source && edge.target === target);
		if (source === target) {
			setToastConfig({
				isToastVisiible: true,
				toastMessage: labels.flowChartNewSteps.selfConnectFailMessage,
				toastMessageType: ToastMessageVariants.Warning
			});
			setIsFlowchartSelectable(true);
			return false;
		} else if (duplicateEdge) {
			setToastConfig({
				isToastVisiible: true,
				toastMessage: labels.flowChartNewSteps.connectionDuplicateMessage,
				toastMessageType: ToastMessageVariants.Warning
			});
			setIsFlowchartSelectable(true);
			return false;
		}
		return true;
	};

	const onConnect = useCallback(
		(connection) => {
			if (isFlowchartSelectable) {
				setIsFlowchartSelectable(false);
				if (validateUpcomingConnection(connection)) {
					saveConnection(connection);
					setIsHandlesVisibileOnOtherNodes(false);
				}
			}
		},
		[setEdges, takeSnapshot, flowChart]
	);

	const onDelete = ({nodes, edges}) => {
		takeSnapshot();
	};

	function getParamsToUpdateStepPosition(node) {
		let xyCoordinateValue = `{ "x":${node.position.x}, "y":${node.position.y} }`;
		let data = {
			attributeJson: xyCoordinateValue,
			columnguid: node.parentId
		};

		let patchDocument = [];
		Object.keys(data).forEach((key) => {
			patchDocument.push({
				op: 'replace',
				path: `/${key}`,
				value: data[key]
			});
		});

		return patchDocument;
	}

	const onNodeDragStart = (evt, node) => {
		dragRef.current = node;
		if (isConcurrency) {
			setIsConcurrency(false);
			getFlowChartDetails().then(() => {
				setIsLoading(false);
				setIsNodeDragStop(true);
			});
		}
	};

	const onNodeDragStop = useCallback(
		(cb, node) => {
			setIsConcurrency(true);

			if (!node.parentId) {
				return;
			}

			if (isNodeDragStop) {
				onNodeDragConcurrency(node);
				setIsNodeDragStop(false);
			}
		},
		[getIntersectingNodes, getNodes, setNodes]
	);

	function onNodeDragConcurrency(node) {
		if (_.isEmpty(node) || _.isNull(node) || !node.parentId) {
			return;
		}

		setIsLoading(false);

		let nodePosition = {x: node.position.x, y: node.position.y};
		let isOverlapped = false;		

		const allNodes = getNodes();
		const intersectionNodes = getIntersectingNodes(node);
		const groupNode = intersectionNodes.filter((n) => n.type === flowchartNodeType.flowChartColumn)[0];
		const columnCount = intersectionNodes.filter((n) => n.type === flowchartNodeType.flowChartColumn).length;

		if (!groupNode || columnCount > 1) {
			isOverlapped = true;
		} 
		if (!isOverlapped) {
			if (node.parentId !== groupNode?.id) {
				// When dragged from other column - set the upper padding to 6rem
				const columnOfDraggedNode = allNodes.find((findNode) => findNode.id === node?.parentId);
				nodePosition.x = columnOfDraggedNode?.position?.x + node?.position?.x - groupNode?.position?.x;
				nodePosition.y = node?.position?.y < cssPixel.SixRem ? cssPixel.SixRem : node?.position?.y; //To set the upper padding of 6 rem
			}
			else {
				// When dragged within the same column - set the upper padding to 6rem
				if (nodePosition.y < cssPixel.SixRem) {
					nodePosition.y = cssPixel.SixRem;
				}
			}

			//Set 1rem padding from left and right border - START
			if (nodePosition.x < cssPixel.OneRem) {
				nodePosition.x = cssPixel.OneRem;
			}
			
			if (nodePosition.x > groupNode?.measured?.width - node.measured?.width - cssPixel.OneRem) {
				nodePosition.x = groupNode?.measured?.width - node.measured?.width - cssPixel.OneRem;
			}
			//Set 1rem padding from left and right border - END			

			const sourceLeft = nodePosition.x;
			const sourceRight = nodePosition.x + node.measured.width;
			const sourceTop = nodePosition.y;
			const sourceBottom = nodePosition.y + node.measured.height;

			const targetNode = intersectionNodes.find((n) => n.type === flowchartNodeType.flowChartStep);

			if (targetNode) {
				const targetLeft = targetNode.position?.x;
				const targetRight = targetNode.position?.x + targetNode.measured?.width;
				const targetTop = targetNode.position?.y;
				const targetBottom = targetNode.position?.y + targetNode.measured?.height;

				isOverlapped = targetLeft < sourceRight || targetRight > sourceLeft || targetTop < sourceBottom || targetBottom > sourceTop;
			}

			if (!isOverlapped) {
				//This is for checking overlapping of the Column bottom border
				isOverlapped =
					nodePosition.y < groupNode?.position.y + cssPixel.ThreeDot125Rem ||
					(node.parentId === groupNode?.id && nodePosition.x < groupNode?.position.x && groupNode?.position.x === 0);

				if (!isOverlapped) {
					isOverlapped = node.position.y + node.measured.height > groupNode?.measured.height;
					if (isOverlapped) {
						isOverlapped = false;
					}
				}
			}
		}

		if (isOverlapped) {
			nodePosition = dragRef.current.position;
		}

		const updatedNodes = allNodes.map((n) => {
			if (n.id === node.id) {
				let newUpdatedNode;
				if (isOverlapped) {
					newUpdatedNode = {
						...n,
						position: nodePosition
					};
				} else {
					if (node.parentId !== groupNode?.id) {
						newUpdatedNode = {
							...n,
							position: nodePosition,
							data: {
								...n.data,
								name: getCssClassNameForFlowChartSteps(groupNode),
								isLoading: true
							},
							draggable: false,
							parentId: groupNode?.id
						};
					} else {
						newUpdatedNode = {
							...n,
							position: nodePosition,
							data: {
								...n.data,
								isLoading: true
							},
							draggable: false
						};
					}

					updateNode(newUpdatedNode);
					const patchDocument = getParamsToUpdateStepPosition(newUpdatedNode);
					dispatch(editStep(engagementId, flowchartId, node.flowchartStepId, patchDocument))
						.catch((err) => {
							dispatch(clearErrors());
							setToastConfig({
								isToastVisiible: true,
								toastMessage: err.response.data.errors.Error[0],
								toastMessageType: ToastMessageVariants.Error
							});
						})
						.finally(() => {
							getFlowChartDetails();
						});
				}
				return newUpdatedNode;
			}

			return n;
		});

		setNodes(updatedNodes);

		dragRef.current = null;
	}

	const setInitRef = (rfInstance) => {
		initRef.current = rfInstance;
	};

	const saveConnection = (connection, edge = null, connectorType = null, connectorId = null) => {
		let connectionObject = connection ? connection : edge;
		if (!_.isNull(connectionObject) && !_.isUndefined(connectionObject)) {
			let flowchartNodes = flowChart?.data ? flowChart?.data[0]?.flowchartSteps : [];
			let flowchartStepId =
				flowchartNodes?.find((step) => step.flowchartStepGuid === connectionObject?.source)?.id || '';
			let targetFlowchartStepId =
				flowchartNodes?.find((step) => step.flowchartStepGuid === connectionObject?.target)?.id || '';
			let sourcePosition = flowChartNodeConnectionPositions?.find((pos) => pos.id == connectionObject.sourceHandle)
				?.position;
			let targetPosition = flowChartNodeConnectionPositions?.find((pos) => pos.id == connectionObject.targetHandle)
				?.position;
			let sourceConnectorId;
			let targetConnectorId;
			if (_.isNull(connectorType)) {
				sourceConnectorId = flowchartConnectorTypes['pointertype'];
				targetConnectorId = flowchartConnectorTypes['default'];
			} else {
				if (connectorType === 'source') {
					sourceConnectorId = connectorId;
					targetConnectorId =
						connectionObject.markerEnd === 'dot-marker'
							? flowchartConnectorTypes['pointertype']
							: flowchartConnectorTypes['default'];
				} else if (connectorType === 'target') {
					targetConnectorId = connectorId;
					sourceConnectorId =
						connectionObject.markerStart === 'dot-marker'
							? flowchartConnectorTypes['pointertype']
							: flowchartConnectorTypes['default'];
				}
			}
			let saveConnectionPayload = {
				flowchartStepTargetStepGuid: uuidv4(),
				sourceFlowchartConnectorTypeId: sourceConnectorId,
				sourceFlowchartConnectorPosition: sourcePosition,
				targetFlowchartStepId: targetFlowchartStepId,
				targetFlowchartConnectorTypeId: targetConnectorId,
				targetFlowchartConnectorPosition: targetPosition,
				documentId: documentId
			};
			dispatch(saveFlowChartConnection(engagementId, flowchartId, flowchartStepId, saveConnectionPayload))
				.then(() => {
					getFlowChartDetails();
					setToastConfig({
						isToastVisiible: true,
						toastMessage: labels.flowChartNewSteps.connectionSuccessMessage,
						toastMessageType: ToastMessageVariants.Success
					});
					setIsFlowchartSelectable(true);
				})
				.catch(() => {
					dispatch(clearErrors());
					setToastConfig({
						isToastVisiible: true,
						toastMessage: labels.flowChartNewSteps.connectionFailedMessage,
						toastMessageType: ToastMessageVariants.Error
					});
					setIsFlowchartSelectable(true);
				});
		}
	};

	const removeEdge = (id, edgeForUpdation = null, connectorType = null, connectorId = null) => {
		setIsEdgeDeleting(true);
		setIsFlowchartSelectable(false);
		takeSnapshot();
		handleEdgeDeletion(id, edgeForUpdation, connectorType, connectorId);
	};

	const handleEdgeDeletion = (id, edgeForUpdation, connectorType, connectorId) => {
		let availableEdges = getEdges();
		if (!_.isNull(availableEdges) && !_.isUndefined(availableEdges)) {
			let edgeFordeletion = availableEdges.find((ed) => ed.id === id);
			let flowchartNodes = flowChart?.data ? flowChart?.data[0]?.flowchartSteps : [];
			let flowchartStepId =
				flowchartNodes?.find((step) => step.flowchartStepGuid === edgeFordeletion?.source)?.id || '';
			let targetFlowchartStepId =
				flowchartNodes?.find((step) => step.flowchartStepGuid === edgeFordeletion?.target)?.id || '';
			dispatch(removeFlowChartConnection(engagementId, flowchartId, flowchartStepId, targetFlowchartStepId))
				.then(() => {
					if (_.isNull(edgeForUpdation)) {
						getFlowChartDetails();
						setToastConfig({
							isToastVisiible: true,
							toastMessage: labels.flowChartNewSteps.connectionDeleteSuccessMessage,
							toastMessageType: ToastMessageVariants.Success
						});
					}
					setIsFlowchartSelectable(true);
					let filteredEdges = availableEdges?.filter((edge) => edge.id !== id);
					setEdges([...new Set(filteredEdges)]);
					setIsEdgeDeleting(false);
					!_.isNull(edgeForUpdation) && saveConnection(null, edgeForUpdation, connectorType, connectorId);
					return true;
				})
				.catch(() => {
					dispatch(clearErrors());
					setToastConfig({
						isToastVisiible: true,
						toastMessage: labels.flowChartNewSteps.connectionDeleteFailMessage,
						toastMessageType: ToastMessageVariants.Error
					});
					setIsEdgeDeleting(false);
					return false;
				});
		}
	};

	const handleEdgePropertyUpdate = (edgeId, connectorType, connectorId) => {
		let availableEdges = getEdges();
		let edgeForUpdation = availableEdges.find((ed) => ed.id === edgeId);
		edgeForUpdation = {
			...edgeForUpdation,
			[connectorType === 'source' ? 'markerStart' : 'markerEnd']: connectorId == 2 ? 'dot-marker' : markerEnd
		};
		removeEdge(edgeId, edgeForUpdation, connectorType, connectorId);
	};

	const onDeleteNode = (id) => {
		takeSnapshot();
		setNodes((nodes) => nodes.filter((node) => node.id !== id));
	};

	const sortParents = (nodes) => {
		if (nodes[0] === flowchartNodeType.flowChartColumn) {
			return nodes;
		}
		const parents = nodes.filter((node) => node.type === flowchartNodeType.flowChartColumn);
		const children = nodes.filter((node) => node.type !== flowchartNodeType.flowChartColumn);
		return [...parents, ...children];
	};

	const setFitViewAttribute = () => {
		setViewport({x: 0, y: 0, zoom: 0.4});
	};

	const onToogleInteractivityClickSlash = () => {
		setIsFlowchartSelectable(false);
	};

	const onToogleInteractivityClickLock = () => {
		setIsFlowchartSelectable(true);
	};

	const resetToastConfig = () => {
		setToastConfig({
			isToastVisiible: false,
			toastMessage: '',
			toastMessageType: ''
		});
	};

	const onPaneClickHandler = () => {
		setEdges((edges) => edges.map((edge) => ({...edge, data: {...edge.data, isPanelClicked: true}})));
		setNodes((nodes) => nodes.map((node) => ({...node, data: {...node.data, isPanelClicked: true}})));
	};

	const onEdgeClickHandler = (event, edge) => {
		updateEdge(edge?.id, (oldEdge) => ({data: {...oldEdge.data, isClicked: true}}));
	};

	return (
		<ReactFlowDiv isHandlesVisible={isHandlesVisibileOnOtherNodes}>
			{toastConfig.isToastVisiible && (
				<StyledMotifToast
					variant={toastConfig.toastMessageType}
					className="toast-banner"
					onClose={() => {
						resetToastConfig();
					}}
					children={toastConfig.toastMessage}
				/>
			)}
			{(isRendering || isEdgeDeleting) && (
				<section className="parentloader">
					<section className="loader">
						<ProgressBar isIndeterminate circle hideLabel />
					</section>
					<section className="greywrapper"></section>
				</section>
			)}
			<ReactFlow
				nodes={nodes}
				edges={edges}
				onNodesChange={onNodesChange}
				onEdgesChange={onEdgesChange}
				onConnect={onConnect}
				onConnectStart={setHandleVisibilityonConnectStart}
				onConnectEnd={setHandleVisibilityonConnectEnd}
				nodeTypes={nodeTypes}
				edgeTypes={edgeTypes}
				onInit={setInitRef}
				onDelete={onDelete}
				onNodeDragStart={onNodeDragStart}
				onNodeDragStop={onNodeDragStop}
				connectionMode={'loose'}
				onPaneClick={onPaneClickHandler}
				onEdgeClick={(event, edge) => onEdgeClickHandler(event, edge)}
				panOnScroll
				zoomOnDoubleClick={false}
				elementsSelectable={isFlowchartSelectable}
				nodesConnectable={isFlowchartSelectable}
				nodesDraggable={isFlowchartSelectable}
				minZoom={0.3}
				maxZoom={2}
			>
				<Controls showInteractive={false} showZoom={false} showFitView={false}>
					<MotifIcon className="fb-icon" title={labels.zoomIn} src={IconoirPlus} onClick={zoomIn} />
					<MotifIcon className="fb-icon" title={labels.zoomOut} src={IconoirMinus} onClick={zoomOut} />
					<MotifIcon
						className="fb-icon"
						title={labels.fitView}
						src={IconoirFrameMinusIn}
						onClick={setFitViewAttribute}
					/>
					{isFlowchartSelectable && (
						<MotifIcon
							className="fb-icon"
							id="iconoirLockSlash"
							title={labels.toogleInteractivity}
							src={IconoirLockSlash}
							onClick={onToogleInteractivityClickSlash}
						/>
					)}
					{!isFlowchartSelectable && (
						<MotifIcon
							className="fb-icon"
							id="iconoirLock"
							title={labels.toogleInteractivity}
							src={IconoirLock}
							onClick={onToogleInteractivityClickLock}
						/>
					)}
				</Controls>
				{/* <Panel position="bottom-center">
					{canUndo ? null : (
						<ButtonDiv position="top-right" className="PanelStyles">
							<button disabled={canUndo} onClick={undo}>
								<span>⤴︝</span> Revert
							</button>
						</ButtonDiv>
					)}
				</Panel> */}
				{isLoading && (
					<section className="parentloader">
						<section className="loader">
							<ProgressBar isIndeterminate circle hideLabel />
						</section>
						<section className="greywrapper"></section>
					</section>
				)}
			</ReactFlow>
		</ReactFlowDiv>
	);
};

export default ReactFlowComponent;

const ReactFlowDiv = styled.section`
	width: 100%;
	height: calc(100% - var(--px-100));
	background: var(--neutrals-00white);
	.react-flow__edges svg {
		max-width: none;
		max-height: none;
	}

	.react-flow__handle.connectionindicator {
		visibility: ${(props) => (props.isHandlesVisible ? 'visible' : 'hidden')};
	}

	.react-flow__controls.vertical {
		position: fixed;
		left: var(--px-20);
		bottom: var(--px-20);
		gap: var(--px-8);
		padding: var(--px-8);
		border-radius: var(--px-8);
		background: var(--neutrals-50);
		.motif-icon {
			border-bottom: 1px solid var(--neutrals-300);
			&:last-child {
				border: none;
			}
			svg {
				width: var(--px-24);
				height: var(--px-24);
				color: var(--neutrals-900);
			}
		}
	}

	.parentloader {
		height: 100%;
		position: absolute;
		width: 100%;
		top: 0;
		pointer-events: all;
		z-index: 999;

		.loader {
			width: 100%;
			height: 100%;
			position: relative;
			z-index: 9999;
			height: 100%;
			pointer-events: none;
		}

		.motif-progress-bar-wrapper {
			width: 100%;
			position: relative;
			top: 50%;
			transform: translateY(-50%);
			.motif-progress-bar {
				svg {
					max-width: 100%;
					max-height: 100%;
				}
			}
		}

		.greywrapper {
			opacity: 0.5;
			position: absolute;
			pointer-events: none;
			width: 100%;
			z-index: 999;
			background: var(--neutrals-00white);
			height: 100%;
			top: 0;
			pointer-events: none;
		}
	}
	.react-flow .react-flow__pane.draggable {
		overflow: auto !important;
	}

	.react-flow__attribution {
		display: none;
	}

	.react-flow__node-FlowchartColumn {
		border: var(--px-1) solid var(--neutrals-200);
		border-left: 0;

		&:first-child {
			border-left: var(--px-1) solid var(--neutrals-200);
		}
	}

	svg {
		max-width: none;
		max-height: none;
	}

	.LayoutContainer {
		padding: var(--px-10);
		background-color: rgba(249, 249, 250, 1);
		width: var(--px-390);
		height: var(--px-640);
		z-index: -1;
		border: 1px solid var(--neutrals-900);
		position: absolute;
	}

	.LayoutContainer::before {
		content: '';
		position: absolute;
		top: var(--px-48);
		left: 0;
		width: 100%;
		border-top: 1px solid rgba(0, 0, 0, 0.1);
	}
`;

const ButtonDiv = styled.div`
	button {
		background-color: #000;
		color: var(--neutrals-00white);
		border-radius: var(--px-5);
		padding: var(--px-5);
		margin: var(--px-5);
		border: none;
		width: var(--px-100);
		font-size: var(--px-12);
	}
`;

const StyledMotifToast = styled(Toast)`
	&.toast-banner {
		margin-bottom: var(--px-10);
	}
`;
